import { SessionEvent, SessionEventKind } from "@/mds/components/sessions/session.types";
import { SessionEventModelData } from "@/store/sessions/types";
import {
  ThreadReplySummary,
  isNotifiableSessionEvent,
} from "@/store/pages/FloatingAgentPageStore/FloatingAgentPageStore";

const toSessionEvent = (
  event: SessionEventModelData,
  thread?: ThreadReplySummary
): SessionEvent => ({
  id: event.id,
  session_id: event.session_id,
  locally_created_at: event.locally_created_at,
  internal_created_at: event.locally_created_at,
  internal_updated_at: event.locally_created_at,
  ...(thread ? { thread } : {}),
  data: {
    kind: event.kind,
    value: event.value_json,
  } as SessionEvent["data"],
});

/**
 * Bridges main-list sync rows into MDSSession's input shape. Callers pass
 * root events only (thread replies render in the
 * thread pane); roots with replies carry the count for the "X replies" row.
 */
export const exportFloatingAgentSessionEvents = (
  sessionEvents: SessionEventModelData[],
  { threadSummaries }: { threadSummaries?: ReadonlyMap<string, ThreadReplySummary> } = {}
): SessionEvent[] => {
  return sessionEvents.map(event => {
    const summary = threadSummaries?.get(event.id);
    return toSessionEvent(event, summary && summary.replyCount > 0 ? summary : undefined);
  });
};

/** Thread pane input: the root pinned first, replies in chronological order. */
export const exportFloatingAgentThreadEvents = (
  rootEvent: SessionEventModelData,
  replies: SessionEventModelData[]
): SessionEvent[] => {
  return [toSessionEvent(rootEvent), ...replies.map(reply => toSessionEvent(reply))];
};

export type AgentReplyPreview = {
  eventId: string;
  createdAt: string;
  text: string;
};

/**
 * Provenance the backend stamps on a completed agent reply: which client group
 * sent the user message it answers. FOREGROUND names that group; BACKGROUND
 * means no user turn triggered the reply. `value_json` is an untyped record on
 * the generated schema, so the shape is restated here.
 */
type SessionEventNotificationOrigin =
  | { kind: "BACKGROUND" }
  | { kind: "FOREGROUND"; sync_client_group_id: string };

/**
 * Whether a reply answers a user message sent from a different install, and so
 * should not notify here.
 *
 * Only an explicit FOREGROUND origin naming another client group suppresses. A
 * missing, null, or BACKGROUND origin notifies, keeping events that predate the
 * backend field — and every SYSTEM_NOTIFICATION_V1, which never carries one —
 * behaving as before. An unresolved local client group notifies for the same
 * reason: a slow cache read must not silently swallow a notification.
 *
 * Note this compares client *groups*, which are per-install: every tab and the
 * desktop widget window share one. It answers "did this install send the
 * message", not "did this window". Which window of a matching install surfaces
 * the reply is left to the caller's focus and panel state.
 */
export const isForeignForegroundReply = (
  event: SessionEventModelData,
  localClientGroupId: string | null
): boolean => {
  if (localClientGroupId === null) return false;

  const value = event.value_json as {
    notification_origin?: SessionEventNotificationOrigin | null;
  } | null;
  const origin = value?.notification_origin;
  if (!origin || origin.kind !== "FOREGROUND") return false;

  return origin.sync_client_group_id !== localClientGroupId;
};

const isUserAuthoredEvent = (event: SessionEventModelData): boolean =>
  event.kind === SessionEventKind.UserActionV1;

/**
 * The latest event the read watermark must cover.
 *
 * Deliberately broader than isNotifiableSessionEvent: the watermark advances
 * past every non-user event — thinking steps, digests, artifact-style rows —
 * so it can never cover fewer kinds than the unread predicate. Narrowing this
 * scan below the unread predicate would let suppressed-but-scanned events
 * resurface as unread after the widget collapses.
 *
 * Streaming snapshots are excluded for the same reason they are not unread:
 * the committed event reuses their id and timestamp, so advancing past a
 * partial would mark the finished reply seen before it ever arrived.
 */
export const findLatestFloatingAgentSeenEventAt = (
  sessionEvents: SessionEventModelData[]
): string | null => {
  let latestSeenEventAt: string | null = null;

  for (const event of sessionEvents) {
    if (isUserAuthoredEvent(event)) continue;
    if (event.is_partial) continue;
    if (latestSeenEventAt !== null && event.locally_created_at <= latestSeenEventAt) continue;

    latestSeenEventAt = event.locally_created_at;
  }

  return latestSeenEventAt;
};

/**
 * All previewable agent/system replies, oldest first. Operator replies are
 * AGENT_PLANNING_V1 events carrying MARKDOWN_V1 parts; SYSTEM_NOTIFICATION_V1
 * events use the same parts shape for system-authored notices.
 */
export const findAgentReplyPreviews = (
  sessionEvents: SessionEventModelData[]
): AgentReplyPreview[] => {
  const previews: AgentReplyPreview[] = [];

  for (const event of sessionEvents) {
    if (!isNotifiableSessionEvent(event)) continue;
    // Streaming snapshots have no committed row yet; they render in the
    // transcript but must not raise a peek bubble or the arrival ding.
    if (event.is_partial) continue;

    const value = event.value_json as {
      parts?: { kind?: string; content?: string }[];
    } | null;
    const text = (value?.parts ?? [])
      .filter(part => part.kind === "MARKDOWN_V1" && typeof part.content === "string")
      .map(part => part.content)
      .join("\n")
      .trim();
    if (text.length === 0) continue;

    previews.push({ eventId: event.id, createdAt: event.locally_created_at, text });
  }

  return previews.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
};

/** Latest text preview, for callers that need a single bubble target. */
export const findLatestAgentReplyPreview = (
  sessionEvents: SessionEventModelData[]
): AgentReplyPreview | null => {
  const previews = findAgentReplyPreviews(sessionEvents);
  return previews.length > 0 ? previews[previews.length - 1] : null;
};
